import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, filter, take, timeout } from 'rxjs/operators';
import { ComponentDataSourceService, DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../../environment/environment';
import { EventDTO } from '../../../../dto/event/event.dto';
import { Router } from '@angular/router';
import { AuthService } from '../../../auth/auth.service';
import { ParticipateEventContextService } from '../../../participate-event-context.service';
import { UserService } from '../../../user/user.service';
import {
  analyzePathModuleParticipationHints,
  buildCompletedWarningHtml,
  getEventModuleIds,
  resolveStatusYearForParticipate,
} from './event-participate-path-module-hints.util';

function mapCateringValue(catering?: string): string {
  switch (catering) {
    case 'self_catering':
      return 'selv';
    case 'catering_included':
      return 'lokation';
    case 'catering_not_included':
      return 'irrelevant';
    default:
      return 'irrelevant';
  }
}

function extractEventGuidFromUrl(
  router: Router,
  participateContext: ParticipateEventContextService
): string | null {
  return participateContext.resolveEventGuid(router);
}

function isOrgSettingTrue(value: unknown): boolean {
  return value !== false && value !== 'false' && value !== 0;
}

function resolveOrgId(eventData: EventDTO): number | null {
  const eventAny = eventData as unknown as Record<string, unknown>;
  const orgId = eventData.organizations_id ?? eventData.organization?.id ?? eventAny['organization_id'];
  if (orgId == null) {
    return null;
  }
  const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId), 10);
  return Number.isFinite(n) ? n : null;
}

const PATH_HINT_FORM_DEFAULTS: Record<string, unknown> = {
  show_path_module_completed_warning: false,
  path_module_completed_warning_html: '',
  path_module_future_signups: [],
};

/** Wait until auth bootstrap finished so catering defaults can load for logged-in users. */
function whenAuthReadyForParticipateForm$(
  authService: AuthService
): Observable<void> {
  if (authService.isAuthenticated && authService.getCurrentUser()?.uuid) {
    return of(undefined);
  }
  return authService.authStateReady$.pipe(
    filter((ready) => ready),
    take(1),
    timeout(15000),
    map(() => undefined),
    catchError(() => of(undefined))
  );
}

function fetchAllowSelfExcludeParticipation$(
  service: ComponentDataSourceService,
  authService: AuthService,
  orgId: number | null
): Observable<boolean> {
  if (orgId == null || !authService.isAuthenticated) {
    return of(true);
  }
  const obs = service.executeDataSource('org_settings', { organizationId: orgId });
  if (!obs) {
    return of(true);
  }
  return obs.pipe(
    map((res: { data?: Record<string, unknown> }) =>
      isOrgSettingTrue(res?.data?.['events_allow_self_exclude_participation'])
    ),
    catchError(() => of(true))
  );
}

function mergePathModuleParticipationHints$(
  service: ComponentDataSourceService,
  authService: AuthService,
  eventData: EventDTO,
  base: Record<string, unknown>
): Observable<Record<string, unknown>> {
  const withDefaults = { ...PATH_HINT_FORM_DEFAULTS, ...base };
  if (getEventModuleIds(eventData).length === 0) {
    return of(withDefaults);
  }

  return whenAuthReadyForParticipateForm$(authService).pipe(
    switchMap(() => {
      if (!authService.isAuthenticated) {
        return of(withDefaults);
      }
      const userUuid = authService.getCurrentUser()?.uuid;
      if (!userUuid) {
        return of(withDefaults);
      }

      const userService = service.injector.get(UserService);
      const year = resolveStatusYearForParticipate(eventData, new Date());

      return userService.getUserByUuid(userUuid, year).pipe(
        map((user) => {
          const paths = userService.educationalPathsFromUser(user);
          const hints = analyzePathModuleParticipationHints(eventData, paths);
          const html = buildCompletedWarningHtml(hints.completedOverlaps);
          return {
            ...withDefaults,
            show_path_module_completed_warning: hints.completedOverlaps.length > 0,
            path_module_completed_warning_html: html,
            path_module_future_signups: hints.futureRegistrations,
          };
        }),
        catchError(() => of(withDefaults))
      );
    })
  );
}

function mergeCateringPreferences$(
  service: ComponentDataSourceService,
  authService: AuthService,
  base: Record<string, unknown>
): Observable<Record<string, unknown>> {
  if (base['catering'] !== 'lokation') {
    return of(base);
  }

  return whenAuthReadyForParticipateForm$(authService).pipe(
    switchMap(() => {
      if (!authService.isAuthenticated) {
        return of(base);
      }
      const userUuid = authService.getCurrentUser()?.uuid;
      if (!userUuid) {
        return of(base);
      }
      return service.http
        .get<{ catering_preferences_form?: string[] }>(
          `${environment.apiUrl}/users/${userUuid}/catering-preferences`
        )
        .pipe(
          map((prefs) => {
            const choices = prefs?.catering_preferences_form;
            if (Array.isArray(choices) && choices.length > 0) {
              return { ...base, catering_choice: choices };
            }
            return base;
          }),
          catchError(() => of(base))
        );
    })
  );
}

export const event_participate_form_data = (service: ComponentDataSourceService): DataSourceFunction => ({
  name: 'event_participate_form_data',
  description: 'Event participate form defaults including user catering preferences and org participation settings',
  execute: (_params?: LoadDataParams): Observable<Record<string, unknown>> => {
    const router = service.injector.get(Router);
    const authService = service.injector.get(AuthService);
    const participateContext = service.injector.get(ParticipateEventContextService);
    const eventGuid = extractEventGuidFromUrl(router, participateContext);

    const fallback = {
      catering: 'irrelevant',
      allow_self_exclude_participation: true,
      participation_type: 'part_selfonly',
      self_participate: 'true',
      ...PATH_HINT_FORM_DEFAULTS,
    };

    if (!eventGuid) {
      return of(fallback);
    }

    return service.http.get<EventDTO>(`${environment.apiUrl}/events/${eventGuid}`).pipe(
      switchMap((eventData) => {
        const formData: Record<string, unknown> = {
          catering: mapCateringValue(eventData.catering),
          participation_type: 'part_selfonly',
          self_participate: 'true',
        };

        const orgId = resolveOrgId(eventData);

        return fetchAllowSelfExcludeParticipation$(service, authService, orgId).pipe(
          map((allowSelfExclude) => ({
            ...formData,
            allow_self_exclude_participation: allowSelfExclude,
          })),
          switchMap((withOrg) =>
            mergePathModuleParticipationHints$(service, authService, eventData, withOrg)
          ),
          switchMap((withHints) =>
            mergeCateringPreferences$(service, authService, withHints)
          )
        );
      }),
      catchError(() => of(fallback))
    );
  }
});
